/**
 * Nomenclature:
 * <dl>
 * 	<dt>OpenSeadragon coordinates</dt>
 *  <dd>The coordinates used by the OpenSeadragon library. In this system The full unrotated image occupies the rectangle (0,0,1,imageHeight/imageWidth)</dl>
 *  <dt>Canvas coordinates </dt>
 *  <dd>The html canvas coordinates ranging from 0 to width/height of the html canvas object</dl>
 *  <dt>Original image coordinates</dt>
 *  <dd>The coordinates of the original, unrotated image, ranging from 0 to the original image width/height</dl>
 *  <dt>Rotated original image coordinates</dt>
 *  <dd>The coordinates in thebounding rectangle of the original image if it was rotated by the viewport rotation</dl>
 * </dl>
 * 
 */
const CoordinateConversion = {
    
    /**
     * Scale the given point or rectangle in the original image to OpenSeadragon coordinates
     * This is the inverse of scaleToImage
     */
    scaleToOpenSeadragon : function(coords, viewer, originalImageSize) {
        originalImageSize = this.getAsDimension(originalImageSize);
        coords = this.convertToOpenSeadragonObject(coords);
    	var displayImageSize = viewer.world.getItemAt(0).source.dimensions;
        var scale = originalImageSize.x/displayImageSize.x;
        coords = coords.times(1/displayImageSize.x);
        coords = coords.times(1/scale);     
        return coords;
    },
    
    /**
     * Scale the given point or rectangle in OpenSeadragon coordinates to original image coordinates
     * This is the inverse of scaleToOpenseadragon
     */
    scaleToImage : function(coords, viewer, originalImageSize) {
        originalImageSize = this.getAsDimension(originalImageSize);
        coords = this.convertToOpenSeadragonObject(coords);
        var displayImageSize = viewer.world.getItemAt(0).source.dimensions;
        var scale = originalImageSize.x/displayImageSize.x;
        coords = coords.times(displayImageSize.x);
        coords = coords.times(scale);
        return coords;
    },

    
    /**
     * Scale the given point or rectangle in OpenSeadragon coordinates of the rotated image to coordinates of the original rotated image.
     * This is the inverse of scaleToOpenSeadragonCoordinates
     */
    scaleToRotatedImage : function(roi, viewer, originalImageSize) {
    	var displayImageSize = viewer.world.getItemAt(0).source.dimensions;
        var displayImageRect = new OpenSeadragon.Rect(0,0,displayImageSize.x, displayImageSize.y);
        var originalImageRect = this.convertToOpenSeadragonObject(this.getAsRect(originalImageSize));
        var rotation = viewer.viewport.getRotation();
        var displayImageRect_rotated = this.getRotatedBounds(displayImageRect, rotation);
        var originalImageRect_rotated = this.getRotatedBounds(originalImageRect, rotation);
        var scale = originalImageRect_rotated.width/displayImageRect_rotated.width;
        roi = roi.times(displayImageSize.x);
        roi = roi.times(scale);
        return roi;
    },
    
    /**
     * input: a rectangle or point in the original image rotated by the current viewport rotation
     * output: the same rectangle/point scaled to OpenSeadragon coordinates of the rotated image
     * This is the inverse of scaleToRotatedImage
     */
    scaleToOpenSeadragonCoordinates : function(roi, viewer, originalImageSize) {
    	var displayImageSize = viewer.world.getItemAt(0).source.dimensions;
        var displayImageRect = new OpenSeadragon.Rect(0,0,displayImageSize.x, displayImageSize.y);
        var originalImageRect = this.convertToOpenSeadragonObject(this.getAsRect(originalImageSize));
        
        var rotation = viewer.viewport.getRotation();
        var displayImageRect_rotated = this.getRotatedBounds(displayImageRect, rotation);
        var originalImageRect_rotated = this.getRotatedBounds(originalImageRect, rotation);
        
        var scale = originalImageRect_rotated.width/displayImageRect_rotated.width;
        roi = this.convertToOpenSeadragonObject(roi);
        roi = roi.times(1/displayImageSize.x);
        roi = roi.times(1/scale);
        return roi;
    },
    
    /**
     * Converts a rectangle from the OpenSeadragon image to the rotated original image
     * This is the inverse of convertRectFromImageToOpenSeadragon
     */
    convertRectFromOpenSeadragonToImage : function(rect, viewer, originalImageSize) {
        var rectInCanvas = ImageView.CoordinateConversion.convertRectFromImageToRotatedImage(rect, viewer);
        var rectInImage = this.scaleToRotatedImage(rectInCanvas, viewer, originalImageSize);
        return rectInImage;
    },
    
    /**
     * Converts a rectangle from the rotated original image to the OpenSeadragon image
     * This is the inverse of convertRectFromOpenSeadragonToImage
     */
    convertRectFromImageToOpenSeadragon : function(rect, viewer, originalImageSize) {
        var rectInCanvas = this.scaleToOpenSeadragonCoordinates(rect, viewer, originalImageSize);
        var rectInOS = this.convertRectFromRotatedImageToImage(rectInCanvas, viewer.viewport.getRotation(), originalImageSize);

        return rectInOS;
    },
    
    /**
     * Converts a rectangle from the rotated original image to the OpenSeadragon image
     * This is the inverse of convertPointFromOpenSeadragonToImage
     */
    convertPointFromImageToOpenSeadragon : function(point, viewer, originalImageSize) {
    	var rectInOS = this.convertPointFromRotatedImageToImage(point, viewer.viewport.getRotation(), originalImageSize);
        var rectInCanvas = this.scaleToOpenSeadragonCoordinates(rectInOS, viewer, originalImageSize);
        return rectInCanvas;
    },
    
    /**
     * Converts a rectangle from the OpenSeadragon image to the rotated original image
     * This is the inverse of convertPointFromImageToOpenSeadragon
     */
    convertPointFromOpenSeadragonToImage : function(point, viewer, originalImageSize) {
    	var pos_image = this.scaleToImage(point, viewer, originalImageSize);
		var pos_image_rot = this.convertPointFromImageToRotatedImage(pos_image, viewer.viewport.getRotation(), originalImageSize);
        return pos_image_rot;
    },
    
    /**
     * Converts a rectangle from the OpenSeadragon image to the canvas
     */
    convertCoordinatesFromImageToCanvas : function(rect, viewer) {
        var scale = viewer.drawer.context.canvas.width/viewer.viewport.getBoundsNoRotate(true).width;
        scale /= window.devicePixelRatio;
        
        var topLeft = this.convertPointFromImageToCanvas(rect.getTopLeft(), viewer);
        var bottomRight = this.convertPointFromImageToCanvas(rect.getBottomRight(), viewer);
        var centerX = topLeft.x + 0.5*(bottomRight.x-topLeft.x);
        var centerY = topLeft.y + 0.5*(bottomRight.y-topLeft.y);

        var canvasRect = new OpenSeadragon.Rect(centerX-0.5*rect.width*scale, centerY-0.5*rect.height*scale, rect.width*scale, rect.height*scale);
        return canvasRect;
    },
    
    /**
     * Converts a rectangle fom canvas coordinates to the OpenSeadragon image
     */
    convertCoordinatesFromCanvasToImage : function(rect, viewer) {
        
        var scale = viewer.drawer.context.canvas.width/viewer.viewport.getBoundsNoRotate(true).width;
        scale /= window.devicePixelRatio;
        
        var topLeft = this.convertPointFromCanvasToImage(rect.getTopLeft(), viewer);
        var bottomRight = this.convertPointFromCanvasToImage(rect.getBottomRight(), viewer);
        var centerX = topLeft.x + 0.5*(bottomRight.x-topLeft.x);
        var centerY = topLeft.y + 0.5*(bottomRight.y-topLeft.y);
        var canvasRect = new OpenSeadragon.Rect(centerX-0.5*rect.width/scale, centerY-0.5*rect.height/scale, rect.width/scale, rect.height/scale);
        return canvasRect;
    },

    /**
     * Converts a point in the OpenSeadragon coordinate system (of the unrotated image) to the coordinate system of the canvas
     * This is affected by window.devicePixelRatio
     * This is the inverse of {@link ImageView.CoordinateConversion.convertPointFromCanvasToImage}
     */
    convertPointFromImageToCanvas : function(point, viewer) {
    	
        var canvasWidth = viewer.drawer.context.canvas.width;
        var viewportWidth = viewer.viewport.getBoundsNoRotate(true).width;

        var scale = canvasWidth/viewportWidth;
        scale /= window.devicePixelRatio;
        var aspectRatio = viewer.source.width/viewer.source.height;
        var rotation = viewer.viewport.getRotation();
        var imageTopLeft_fromImageCenter = new OpenSeadragon.Point(0.5, 0.5/aspectRatio).times(-1);
        var canvasCenter_fromImageTopLeft = viewer.viewport.getCenter(true);
        var canvasCenter_fromCanvasTopLeft = new OpenSeadragon.Point(viewer.viewport.getBoundsNoRotate(true).width/2.0, viewer.viewport.getBoundsNoRotate(true).height/2.0);
        
        var canvasCenter_fromImageCenter = imageTopLeft_fromImageCenter.plus(canvasCenter_fromImageTopLeft);
        var canvasCenter_fromImageCenter_rotated = this.rotate(canvasCenter_fromImageCenter, rotation, true);
        
        var imageCenter_fromCanvasTopLeft = canvasCenter_fromCanvasTopLeft.minus(canvasCenter_fromImageCenter_rotated);

        var point_fromImageCenter = imageTopLeft_fromImageCenter.plus(point);
        var point_fromImageCenter_rotated = this.rotate(point_fromImageCenter, rotation, true);
        
        var point_FromCanvasTopLeft = imageCenter_fromCanvasTopLeft.plus(point_fromImageCenter_rotated);

        var p = point_FromCanvasTopLeft.times(scale);
        
        return p;
    },
    
    /**
     * Converts a point in the canvas coordinate system to the OpenSeadragon coordinate system (of the unrotated image)
     * This is affected by window.devicePixelRatio
     * This is the inverse of {@link ImageView.CoordinateConversion.convertPointFromImageToCanvas}
     */
    convertPointFromCanvasToImage : function(point, viewer) {
        var scale = viewer.drawer.context.canvas.width/viewer.viewport.getBoundsNoRotate(true).width;
        scale /= window.devicePixelRatio;
        
        var aspectRatio = viewer.source.width/viewer.source.height;
        var rotation = viewer.viewport.getRotation();
        var imageTopLeft_fromImageCenter = new OpenSeadragon.Point(0.5, 0.5/aspectRatio).times(-1);
        var canvasCenter_fromImageTopLeft = viewer.viewport.getCenter(true);
        var canvasCenter_fromCanvasTopLeft = new OpenSeadragon.Point(viewer.viewport.getBoundsNoRotate(true).width/2.0, viewer.viewport.getBoundsNoRotate(true).height/2.0);
        
        var canvasCenter_fromImageCenter = imageTopLeft_fromImageCenter.plus(canvasCenter_fromImageTopLeft);
        var canvasCenter_fromImageCenter_rotated = this.rotate(canvasCenter_fromImageCenter, rotation, true);
        
        var imageCenter_fromCanvasTopLeft = canvasCenter_fromCanvasTopLeft.minus(canvasCenter_fromImageCenter_rotated);
        
        var point_fromCanvasTopLeft = point.times(1/scale);
        var point_fromImageCenter_rotated = point_fromCanvasTopLeft.minus(imageCenter_fromCanvasTopLeft);
        var point_fromImageTopLeft = this.rotate(point_fromImageCenter_rotated, rotation, false).minus(imageTopLeft_fromImageCenter);

        return point_fromImageTopLeft;
    },
    
    /**
     * input parameter rect: A rectangle in the coordinate system of the plain unrotated image in OpenSeadragon coordinates
     * output: The same rectangle in rotated image in OpenSeadragon coordinates
     * 
     * Both rectangles are defined by their center and their width and height. Width and height remain constant,
     * while the center is converted into coordinates of the rotated image
     */
    convertRectFromImageToRotatedImage : function(rect, viewer) {
        
        var rotation = viewer.viewport.getRotation();
        var sourceBounds = new OpenSeadragon.Rect(0,0,viewer.source.width, viewer.source.height);
        var sourceBounds_rotated = this.getRotatedBounds(sourceBounds, rotation);
        var aspectRatio_unrotated = sourceBounds.width/sourceBounds.height;
        var aspectRatio_rotated = sourceBounds_rotated.width/sourceBounds_rotated.height;
        
        var imageBounds_unrotated = new OpenSeadragon.Rect(0,0, 1.0, 1/aspectRatio_unrotated); 
        var imageBounds_rotated = this.getRotatedBounds(imageBounds_unrotated, rotation);

        
        var rect_fromTopLeft_unrotated = rect.getCenter();
        var topLeft_fromCenter_unrotated = imageBounds_unrotated.getCenter().times(-1);
        var rect_fromCenter_unrotated = topLeft_fromCenter_unrotated.plus(rect_fromTopLeft_unrotated);

        var rect_fromCenter_rotated = this.rotate(rect_fromCenter_unrotated, rotation, true);

        
        var topLeft_fromCenter_rotated = new OpenSeadragon.Point(imageBounds_rotated.width/2.0, imageBounds_rotated.height/2.0).times(-1);
        var rect_fromTopLeft_rotated = rect_fromCenter_rotated.minus(topLeft_fromCenter_rotated);
        var rect_rotated = new OpenSeadragon.Rect(rect_fromTopLeft_rotated.x-rect.width/2.0, rect_fromTopLeft_rotated.y-rect.height/2.0, rect.width, rect.height);
        return rect_rotated;
    },
    
    /**
     * input parameter rect: A rectangle in OpenSeadragon coordinates as if the image was the actual image rotated by the
     * current viewport rotation
     * output: The same rectangle in the displayed (unrotated) image in OpenSeadragon coordinates
     * 
     * Both rectangles are defined by their center and their width and height. Width and height remain constant,
     * while the center is converted into coordinates of the rotated image
     */
    convertRectFromRotatedImageToImage : function(rect, rotation, imageBounds) {
        var sourceBounds = this.convertToOpenSeadragonObject(this.getAsRect(imageBounds));
        var sourceBounds_rotated = this.getRotatedBounds(sourceBounds, rotation);
        var aspectRatio_unrotated = sourceBounds.width/sourceBounds.height;
        var aspectRatio_rotated = sourceBounds_rotated.width/sourceBounds_rotated.height;
        
        var imageBounds_unrotated = new OpenSeadragon.Rect(0,0, 1.0, 1/aspectRatio_unrotated); 
        var imageBounds_rotated = this.getRotatedBounds(imageBounds_unrotated, rotation);
        var topLeft_fromCenter_unrotated = imageBounds_unrotated.getCenter().times(-1);
        var topLeft_fromCenter_rotated = new OpenSeadragon.Point(imageBounds_rotated.width/2.0, imageBounds_rotated.height/2.0).times(-1);
        
        var rect_fromTopLeft_rotated = rect.getCenter();
        var rect_fromCenter_rotated = topLeft_fromCenter_rotated.plus(rect_fromTopLeft_rotated);

        var rect_fromCenter_unrotated = this.rotate(rect_fromCenter_rotated, rotation, false);

        var rect_fromTopLeft_unrotated = rect_fromCenter_unrotated.minus(topLeft_fromCenter_unrotated);
        var rect_unrotated = new OpenSeadragon.Rect(rect_fromTopLeft_unrotated.x-rect.width/2.0, rect_fromTopLeft_unrotated.y-rect.height/2.0, rect.width, rect.height);
        return rect_unrotated;
    },
    
    /**
     * Converts a point in any image coordinates to the same point in the coordinate system of the bounding rectangle of the same image if it was rotated by the viewport rotation
     */
    convertPointFromImageToRotatedImage : function(point, rotation, imageBounds) {
        
        var imageBounds_rotated = this.getRotatedBounds(imageBounds, rotation);
        imageBounds = this.convertToOpenSeadragonObject(this.getAsRect(imageBounds));

        var topLeft_fromCenter_unrotated = imageBounds.getCenter().times(-1);
        var topLeft_fromCenter_rotated = new OpenSeadragon.Point(imageBounds_rotated.width/2.0, imageBounds_rotated.height/2.0).times(-1);
        
        var point_fromTopLeft_unrotated = this.convertToOpenSeadragonObject(point);
        var point_fromCenter_unrotated = point_fromTopLeft_unrotated.plus(topLeft_fromCenter_unrotated);

        var point_fromCenter_rotated = point_fromCenter_unrotated.rotate(rotation);
        var point_fromTopLeft_rotated = point_fromCenter_rotated.minus(topLeft_fromCenter_rotated);
        
        return point_fromTopLeft_rotated;
    },
    
    /**
     * Converts a point in the coordinate system of the bounding rectangle of a rotated image to the coordinate system of the image itself.
     */
    convertPointFromRotatedImageToImage : function(point, rotation, imageBounds) {

        var imageBounds_rotated = this.getRotatedBounds(imageBounds, rotation);
        imageBounds = this.convertToOpenSeadragonObject(this.getAsRect(imageBounds));

        var topLeft_fromCenter_unrotated = imageBounds.getCenter().times(-1);
        var topLeft_fromCenter_rotated = new OpenSeadragon.Point(imageBounds_rotated.width/2.0, imageBounds_rotated.height/2.0).times(-1);
       
        var point_fromTopLeft_rotated = this.convertToOpenSeadragonObject(point);
        var point_fromCenter_rotated = point_fromTopLeft_rotated.plus(topLeft_fromCenter_rotated);
        var point_fromCenter_unrotated = point_fromCenter_rotated.rotate(-rotation);
        var point_fromTopLeft_unrotated = point_fromCenter_unrotated.minus(topLeft_fromCenter_unrotated);

        return point_fromTopLeft_unrotated;
    },
    
    /**
     * Converts a rectangle in a rotated image to the rectangle in the unrotated image
     */
    convertImageAreaFromRotatedImageToImage : function(area, rotation, imageBounds) {
        let topLeft = new OpenSeadragon.Point(area.x, area.y);
        let bottomRight = new OpenSeadragon.Point(area.x + area.width, area.y + area.height);
        
        let topLeft_unrotated = this.convertPointFromRotatedImageToImage(topLeft, rotation, imageBounds);
        let bottomRight_unrotated = this.convertPointFromRotatedImageToImage(bottomRight, rotation, imageBounds);
        
        let top = Math.min(topLeft_unrotated.y, bottomRight_unrotated.y);
        let left = Math.min(topLeft_unrotated.x, bottomRight_unrotated.x);
        let bottom = Math.max(topLeft_unrotated.y, bottomRight_unrotated.y);
        let right = Math.max(topLeft_unrotated.x, bottomRight_unrotated.x);
        
        let area_unrotated = new OpenSeadragon.Rect(left, top, right-left, bottom-top);
        return area_unrotated;
    },




/**
 * Rotates around the coordinate system origin in clockwise direction. If the 'anticockwise' parameter is set and is true then the rotation direction is reversed.
 * 
 */
    rotate : function(point, degrees, antiClockwise) {

	    var rad = degrees*Math.PI/180.0;
	    
	    var x,y;
	    if(antiClockwise === true) {
	        x = point.x*Math.cos(rad) - point.y*Math.sin(rad);
	        y = point.x*Math.sin(rad) + point.y*Math.cos(rad);
	    } else {
	        x =  point.x*Math.cos(rad) + point.y*Math.sin(rad);
	        y = -point.x*Math.sin(rad) + point.y*Math.cos(rad);
	    }
	    
	    return new OpenSeadragon.Point(x,y);
    },
    
    /**
     * Calculates the bounding rectangle that just encompasses the given rectangle rotated by the given angle in degrees.
     * The given rectangle is assumed to start at coordinates 0,0; and the returned rectangle will be given in the same coordinate frame,
     * i.e. with x and y values holding the offset from the original origin point (x and y are thus always negative, width and height always
     * larger than those of the original rectangle)
     * 
     * @param rect  the rectangle to rotate. must be an object with properties height and width
     * @param degrees   the rotation angle in degrees
     * @return     An OpenSeadragon.Rect containing the rotated rectangle in the original coordinate system
     */
    getRotatedBounds : function(rect, degrees) {
            var width = rect.width === undefined ? rect.x : rect.width;
            var height = rect.height === undefined ? rect.y : rect.height;

            var rad = degrees * Math.PI/180.0;
        
            var sint = Math.abs(Math.sin(rad));
            var cost = Math.abs(Math.cos(rad));

            var hh = (width * sint + height * cost);
            var ww = (width * cost + height * sint);
            
            var w = Math.abs(ww);
            var h = Math.abs(hh);
            
            var dw = w - width;
            var dh = h - height;
            return new OpenSeadragon.Rect(-dw/2.0, -dh/2.0, w, h);
    },
    
    /**
     * If the argument has width and height, return an object which contains width and height as x and y
     */
    getAsDimension : function(rect) {
    		var size =  {
    			x: rect.width === undefined ? rect.x : rect.width,
    	    	y: rect.height === undefined ? rect.y : rect.height,
    		};
    		return this.convertToOpenSeadragonObject(size);
    },
    
    getAsRect : function(coords) {
		var rect =  {
			x: coords.width === undefined ? 0 : coords.x,
	    	y: coords.height === undefined ? 0 : coords.y,			
	    	width: coords.width === undefined ? coords.x : coords.width,
	    	height: coords.height === undefined ? coords.y : coords.height,
		}
		return this.convertToOpenSeadragonObject(rect);
    },
    
    convertToOpenSeadragonObject : function(object) {
    	if(object instanceof OpenSeadragon.Point || object instanceof OpenSeadragon.Rect) {
    		return object;
    	} else if(object.width !== undefined) {
    		return new OpenSeadragon.Rect(object.x, object.y, object.width, object.height);
    	} else if(typeof object === 'string') {
    	    let coords = object.split(",").map( i => i.trim()).map( i => parseFloat(i));
    	    if(coords.length == 2) {
    	        return new OpenSeadragon.Point(coords[0], coords[1]);
    	    } else if(coords.length == 4) {
    	        return new OpenSeadragon.Rect(coords[0], coords[1], coords[2], coords[3]);
    	} else {
    	    console.log("cannot convert ", object);
    	        throw "Cannot convert " + object + " to OpenSeadragon object";
    	    }
    	} else if(object.x !== undefined) {
    		return new OpenSeadragon.Point(object.x, object.y);
    	} else {
    	    console.log("cannot convert ", object);
            throw "Cannot convert " + object + " to OpenSeadragon object";
    	}
    },

}

export default CoordinateConversion;